import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Title from "../dashboard/Title";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {NotificationManager} from "react-notifications";
import {
    Grid,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from "@material-ui/core";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import {useDashboardStyles, BRAND} from "../settings/dashboardStyles";

const useLocalStyles = makeStyles((theme) => ({
    statCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(3),
        background: '#ffffff',
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 12,
        boxShadow: '0 4px 14px rgba(44, 71, 103, 0.05)',
        transition: 'box-shadow 200ms ease, border-color 200ms ease, transform 180ms ease',
        '&:hover': {
            boxShadow: '0 10px 26px rgba(44, 71, 103, 0.10)',
            borderColor: 'rgba(176, 141, 87, 0.45)',
            transform: 'translateY(-1px)',
        },
    },
    statNumber: {
        fontSize: '2.6rem',
        fontWeight: 700,
        color: BRAND.deep,
        lineHeight: 1.1,
        letterSpacing: '-1px',
    },
    statLabel: {
        fontSize: '0.92rem',
        color: BRAND.inkSoft,
        marginTop: theme.spacing(1),
        textAlign: 'center',
        fontWeight: 500,
    },
    chartContainer: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(3),
        background: BRAND.creamSoft,
        borderRadius: 12,
        border: `1px solid ${BRAND.goldSoft}`,
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
            marginTop: theme.spacing(3),
        },
    },
    chartTitle: {
        fontWeight: 700,
        color: BRAND.deep,
        fontSize: '1.1rem',
        marginBottom: theme.spacing(2),
    },
    yearSelectWrap: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(2),
    },
    yearSelect: {
        minWidth: 200,
        background: '#ffffff',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.goldSoft,
        },
    },
    loadingState: {
        padding: theme.spacing(6, 3),
        textAlign: 'center',
        color: BRAND.inkSoft,
        fontSize: '1.05rem',
    },
    autopsyBreakdown: {
        width: '100%',
        marginTop: theme.spacing(2),
    },
    autopsyBreakdownRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        color: BRAND.inkSoft,
        fontSize: '0.82rem',
        lineHeight: 1.35,
        padding: theme.spacing(0.75, 0),
        borderTop: `1px solid ${BRAND.goldSoft}`,
    },
    autopsyBreakdownCount: {
        color: BRAND.deep,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
}));

const COLORS = [BRAND.deep, BRAND.gold, '#6c8a9f', '#d4b685', '#1e3553', '#8a6e42', '#4c6b85', '#c9a576'];

const AUTOPSY_YES_OPTIONS = [
    'ÁNO, Urobili sme zvoz a MÁME POHREB',
    'ÁNO, INÁ pohrebná služba urobila zvoz a MÁME POHREB',
    'ÁNO, urobili sme zvoz a NEMÁME POHREB',
];

export default function Statistics(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();
    const [statistics, setStatistics] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStatistics(selectedYear);
    }, [selectedYear]);

    const loadStatistics = (year) => {
        setLoading(true);
        UserService.getStatistics(year).then(
            (response) => {
                setStatistics(response.data);
                setLoading(false);
            },
            (e) => {
                if (e.response?.status === 401) {
                    logOut(props);
                    NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                } else {
                    NotificationManager.error('Nepodarilo sa načítať dáta!', 'Chyba', 5000);
                }
                setLoading(false);
            }
        );
    };

    // Transform backend data for charts
    const getFuneralsByMonth = () => {
        if (!statistics) return [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
        const monthCount = Array(12).fill(0);
        
        statistics.monthlyData.forEach(item => {
            monthCount[item._id - 1] = item.count;
        });

        return monthNames.map((name, index) => ({
            name,
            pocet: monthCount[index]
        }));
    };

    const getFuneralsByCity = () => {
        if (!statistics) return [];
        return statistics.cityData.map(item => ({
            name: item._id || 'Neuvedené',
            value: item.count
        }));
    };

    const getFuneralsByType = () => {
        if (!statistics) return [];
        return statistics.dTypeData
            .filter(item => item._id)
            .map(item => ({
                name: item._id,
                value: item.count
            }));
    };

    const getTopServices = () => {
        if (!statistics) return [];
        const data = statistics.serviceData.map(item => ({
            name: item._id || 'Neuvedené',
            value: item.totalRevenue,
            count: item.count
        }));
        return data;
    };

    const getTopProducts = () => {
        if (!statistics) return [];
        const data = statistics.productData.map(item => ({
            name: item._id || 'Neuvedené',
            value: item.totalRevenue,
            count: item.count
        }));
        return data;
    };

    const getAutopsyBreakdownCount = (option) => {
        if (!statistics || !statistics.autopsyBreakdown) return 0;
        const item = statistics.autopsyBreakdown.find(value => value._id === option);
        return item ? item.count : 0;
    };

    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
    };

    if (loading || !statistics) {
        return (
            <Container maxWidth="lg" className={classes.pageContainer}>
                <Paper className={classes.paper} elevation={0}>
                    <div className={classes.sectionHeader}>
                        <Title>Štatistiky</Title>
                    </div>
                    <div className={localClasses.loadingState}>Načítavam dáta...</div>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" className={classes.pageContainer}>
            <Paper className={classes.paper} elevation={0}>
                <div className={classes.sectionHeader}>
                    <Title>Štatistiky</Title>
                </div>

                {/* Summary Cards */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.yearCount}</span>
                            <span className={localClasses.statLabel}>Pohrebov za rok {selectedYear}</span>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.totalCount}</span>
                            <span className={localClasses.statLabel}>Pohrebov celkovo</span>
                        </div>
                    </Grid>
                </Grid>

                {/* Additional Statistics Cards */}
                <Grid container spacing={3} style={{marginTop: 8}}>
                    <Grid item xs={12} sm={6} md={3}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.autopsyCount}</span>
                            <span className={localClasses.statLabel}>Pohrebov s pitvou</span>
                            <div className={localClasses.autopsyBreakdown}>
                                {AUTOPSY_YES_OPTIONS.map(option => (
                                    <div key={option} className={localClasses.autopsyBreakdownRow}>
                                        <span>{option}</span>
                                        <span className={localClasses.autopsyBreakdownCount}>{getAutopsyBreakdownCount(option)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.dangerousDiseaseCount}</span>
                            <span className={localClasses.statLabel}>Pohrebov s nebezpečnou chorobou</span>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.cremationCount}</span>
                            <span className={localClasses.statLabel}>Kremačné pohreby</span>
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <div className={localClasses.statCard}>
                            <span className={localClasses.statNumber}>{statistics.graveCount}</span>
                            <span className={localClasses.statLabel}>Pohrebov s hrobom</span>
                        </div>
                    </Grid>
                </Grid>

                {/* Year Filter */}
                <div className={localClasses.yearSelectWrap}>
                    <FormControl variant="outlined" className={localClasses.yearSelect}>
                        <InputLabel id="year-select-label">Vybrať rok</InputLabel>
                        <Select
                            labelId="year-select-label"
                            value={selectedYear}
                            onChange={handleYearChange}
                            label="Vybrať rok"
                        >
                            {statistics.availableYears.map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* Monthly Chart */}
                <div className={localClasses.chartContainer}>
                    <Typography className={localClasses.chartTitle}>
                        Počet pohrebov podľa mesiacov ({selectedYear})
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getFuneralsByMonth()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(176, 141, 87, 0.2)"/>
                            <XAxis dataKey="name" stroke={BRAND.inkSoft}/>
                            <YAxis stroke={BRAND.inkSoft}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey="pocet" fill={BRAND.deep} name="Počet pohrebov" radius={[6, 6, 0, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* City Distribution Chart */}
                <div className={localClasses.chartContainer}>
                    <Typography className={localClasses.chartTitle}>
                        10 miest/obcí podľa počtu pohrebov
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <ResponsiveContainer width="100%" height={400}>
                                <PieChart>
                                    <Pie
                                        data={getFuneralsByCity()}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={120}
                                        dataKey="value"
                                    >
                                        {getFuneralsByCity().map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                        ))}
                                    </Pie>
                                    <Tooltip/>
                                </PieChart>
                            </ResponsiveContainer>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={getFuneralsByCity()}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(176, 141, 87, 0.2)"/>
                                    <XAxis dataKey="name" stroke={BRAND.inkSoft}/>
                                    <YAxis stroke={BRAND.inkSoft}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Bar dataKey="value" fill={BRAND.gold} name="Počet pohrebov" radius={[6, 6, 0, 0]}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </Grid>
                    </Grid>
                </div>

                {/* Deceased Type Chart */}
                <div className={localClasses.chartContainer}>
                    <Typography className={localClasses.chartTitle}>
                        Typ zosnulého
                    </Typography>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={getFuneralsByType()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(176, 141, 87, 0.2)"/>
                            <XAxis dataKey="name" interval={0} angle={-15} textAnchor="end" height={70} stroke={BRAND.inkSoft}/>
                            <YAxis stroke={BRAND.inkSoft}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey="value" fill={BRAND.deep} name="Počet pohrebov" radius={[6, 6, 0, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Top Services Chart */}
                <div className={localClasses.chartContainer}>
                    <Typography className={localClasses.chartTitle}>
                        10 služieb s najvyššími príjmami ({selectedYear})
                    </Typography>
                    <ResponsiveContainer width="100%" height={600}>
                        <BarChart data={getTopServices()} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(176, 141, 87, 0.2)"/>
                            <XAxis type="number" stroke={BRAND.inkSoft}/>
                            <YAxis dataKey="name" type="category" width={400} stroke={BRAND.inkSoft}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey="value" fill={BRAND.deep} name="Celkové príjmy (€)" radius={[0, 6, 6, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Top Products Chart */}
                <div className={localClasses.chartContainer}>
                    <Typography className={localClasses.chartTitle}>
                        10 produktov s najvyššími príjmami ({selectedYear})
                    </Typography>
                    <ResponsiveContainer width="100%" height={600}>
                        <BarChart data={getTopProducts()} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(176, 141, 87, 0.2)"/>
                            <XAxis type="number" stroke={BRAND.inkSoft}/>
                            <YAxis dataKey="name" type="category" width={400} stroke={BRAND.inkSoft}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey="value" fill={BRAND.gold} name="Celkové príjmy (€)" radius={[0, 6, 6, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Paper>
        </Container>
    );
}
